/**
 * ENT-6459 (D1): the supplier claim candidate is a server-side read-model field.
 * `get_my_supplier_claim_candidate()` derives it from the caller's verified email;
 * the browser only parses the payload and never computes it.
 */
export type SupplierClaimCandidateSource = 'invitation' | 'domain_match';
export type SupplierClaimCandidateState =
  | 'unclaimed'
  | 'claimed_by_other'
  | 'managed_requires_approval';
export type SupplierClaimOfferSurface = 'onboarding' | 'plan_selection' | 'app_gate';

export interface SupplierClaimCandidate {
  marketplace_listing_id: string;
  company_name: string | null;
  domain: string;
  source: SupplierClaimCandidateSource;
  claim_state: SupplierClaimCandidateState;
  inviting_organization_name: string | null;
  current_organization_id: string | null;
  current_organization_convertible: boolean;
}

/** Admin read model: the candidate of the organisation's first admin member that has one. */
export interface OrganizationSupplierClaimCandidate extends SupplierClaimCandidate {
  member_email: string | null;
}

export const GET_MY_SUPPLIER_CLAIM_CANDIDATE_RPC = 'get_my_supplier_claim_candidate';
export const RECORD_SUPPLIER_CLAIM_OFFER_SHOWN_RPC = 'record_supplier_claim_offer_shown';
export const ADMIN_GET_ORGANIZATION_SUPPLIER_CLAIM_CANDIDATE_RPC =
  'admin_get_organization_supplier_claim_candidate';

const SOURCES: readonly SupplierClaimCandidateSource[] = ['invitation', 'domain_match'];
const STATES: readonly SupplierClaimCandidateState[] = [
  'unclaimed',
  'claimed_by_other',
  'managed_requires_approval',
];

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Parses the RPC payload. `null` means "no candidate". A payload that does not match the
 * contract throws, so the caller treats it as an error and never shows a false offer.
 */
export function parseSupplierClaimCandidate(value: unknown): SupplierClaimCandidate | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Supplier claim candidate returned an invalid payload.');
  }
  const record = value as Record<string, unknown>;
  const listingId = optionalString(record.marketplace_listing_id);
  const domain = optionalString(record.domain);
  const source = record.source as SupplierClaimCandidateSource;
  const claimState = record.claim_state as SupplierClaimCandidateState;
  if (!listingId || !domain || !SOURCES.includes(source) || !STATES.includes(claimState)) {
    throw new Error('Supplier claim candidate returned an incomplete payload.');
  }
  return {
    marketplace_listing_id: listingId,
    company_name: optionalString(record.company_name),
    domain,
    source,
    claim_state: claimState,
    inviting_organization_name: optionalString(record.inviting_organization_name),
    current_organization_id: optionalString(record.current_organization_id),
    current_organization_convertible: record.current_organization_convertible === true,
  };
}

export function parseOrganizationSupplierClaimCandidate(
  value: unknown,
): OrganizationSupplierClaimCandidate | null {
  const candidate = parseSupplierClaimCandidate(value);
  if (!candidate) return null;
  return {
    ...candidate,
    member_email: optionalString((value as Record<string, unknown>).member_email),
  };
}

/** Tiered fallback: company name, then domain; null means "use the generic phrase". */
export function supplierClaimCompanyLabel(
  candidate: Pick<SupplierClaimCandidate, 'company_name' | 'domain'>,
): string | null {
  return optionalString(candidate.company_name) ?? optionalString(candidate.domain);
}

/**
 * The claim route. The claim page resolves a URL domain to the listing through the canonical and
 * linked domain mapping, but treats a URL domain different from the signed-in email domain as a
 * mismatch; a linked-domain colleague sent to the primary domain lands on the mismatch screen
 * instead of the join form (Codex r4451325823). A candidate always matched on the caller's own
 * verified email domain (can_claim_supplier_listing_domain), so the route carries that domain,
 * derived exactly as the claim page derives it. Without a session email it falls back to the
 * read model's listing domain. Never a URL-supplied domain.
 */
export function claimPageEmailDomain(email: string | null | undefined): string | null {
  const domain = email?.split('@')[1]?.toLowerCase();
  return domain ? domain : null;
}

export function supplierClaimRoute(
  candidate: Pick<SupplierClaimCandidate, 'domain'>,
  callerEmail?: string | null,
): string {
  const domain = claimPageEmailDomain(callerEmail) ?? candidate.domain;
  return `/vendor/claim?domain=${encodeURIComponent(domain)}`;
}
